import { api } from '../lib/api';

/**
 * ContentService - Gerencia operações de geração de conteúdos
 * 
 * ✅ ATUALIZADO: Nova arquitetura com VM + Fluxo Instagram + Placid Editor
 *    - Batch é criado com status "pending"
 *    - VM processa e atualiza status
 *    - Frontend apenas faz polling do batch
 *    - Novo método analyzeInstagram para fluxo automático
 *    - Métodos para edição Placid
 */
export const contentService = {
  /**
   * Criar novo batch de conteúdos
   * 
   * ✅ NOTA: Batch é criado com status "pending"
   *    A VM irá detectar e processar automaticamente
   */
  async createBatch(batchData) {
    const response = await api.post('/api/contents/batches', batchData);
    return response.data;
  },

  /**
   * Listar batches do usuário
   */
  async listBatches(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    
    const url = `/api/contents/batches?${queryParams.toString()}`;
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Obter batch específico
   * 
   * ✅ NOTA: Use este endpoint para polling
   *    A VM atualiza o Cosmos DB diretamente
   */
  async getBatch(batchId) {
    const response = await api.get(`/api/contents/batches/${batchId}`);
    return response.data;
  },

  /**
   * Deletar batch
   */
  async deleteBatch(batchId) {
    const response = await api.delete(`/api/contents/batches/${batchId}`);
    return response.data;
  },

  /**
   * 🆕 Analisar perfil do Instagram
   * 
   * Extrai informações do perfil:
   * - Bio
   * - Cores predominantes das imagens
   * - URLs das imagens recentes
   * - Prompt gerado automaticamente
   * 
   * @param {string} username - Nome de usuário do Instagram (sem @)
   * @returns {Promise<Object>} Dados do perfil analisado
   */
  async analyzeInstagram(username) {
    const response = await api.post('/api/contents/analyze-instagram', {
      username: username.replace('@', '').trim()
    });
    return response.data;
  },

  /**
   * 🆕 Criar batch a partir de perfil Instagram
   * 
   * Método de conveniência que analisa o Instagram e cria o batch em uma chamada
   * 
   * @param {string} username - Nome de usuário do Instagram
   * @param {Object} options - Opções adicionais
   * @returns {Promise<Object>} Batch criado
   */
  async createBatchFromInstagram(username, options = {}) {
    const response = await api.post('/api/contents/batches/from-instagram', {
      username: username.replace('@', '').trim(),
      content_type: options.contentType || 'marketing',
      ...options
    });
    return response.data;
  },

  // ============================================
  // EDITOR CONVERSACIONAL (Fase 4)
  // ============================================

  /**
   * Edita um conteúdo via chat conversacional.
   * O backend chama o ChatEditorAgent (GPT-4.1 + function calling) que pode
   * regenerar a imagem, editá-la cirurgicamente, ajustar caption ou aplicar
   * assets da brand_identity.
   *
   * @param {string} batchId
   * @param {string} contentId
   * @param {string} message - mensagem do usuário em linguagem natural
   * @param {Array} attachments - [{source: 'library' | 'upload', asset_id?: string, base64?: string}]
   * @returns {Promise<{data: {assistant_message, new_image_url?, new_caption?, edits_used, edits_limit, monthly_credits_remaining, ...}}>}
   */
  async editContentViaChat(batchId, contentId, message, attachments = []) {
    const response = await api.post(
      `/api/contents/batches/${batchId}/contents/${contentId}/edit-chat`,
      { message, attachments },
    );
    return response.data;
  },

  /**
   * Polling do status da edição conversacional (job assíncrono).
   * Retorna { data: { edit_job: {status}, result_url, chat_history, edits_used, edits_limit, ... } }
   */
  async getChatEditStatus(batchId, contentId) {
    const response = await api.get(
      `/api/contents/batches/${batchId}/contents/${contentId}/edit-status`,
    );
    return response.data;
  },

  // ============================================
  // MÉTODOS PLACID EDITOR
  // ============================================

  /**
   * Gera um token JWT para o Placid Canvas SDK
   * @param {string} templateUuid - UUID do template Placid
   * @returns {Promise<{access_token: string, template_uuid: string, expires_at: string}>}
   */
  async getPlacidCanvasToken(templateUuid) {
    try {
      const response = await api.post('/api/contents/placid/canvas-token', {
        template_uuid: templateUuid
      });
      
      // ✅ Backend retorna diretamente: { access_token, template_uuid, expires_at }
      // Não está aninhado em "data"
      return response.data;
    } catch (error) {
      console.error('Erro ao gerar token do Canvas:', error);
      throw error;
    }
  },

  /**
   * Faz upload de uma imagem para edição de conteúdo
   * @param {string} contentId - ID do content
   * @param {File} file - Arquivo de imagem
   * @returns {Promise<{url: string, file_name: string, file_size: number, content_id: string}>}
   */
  async uploadEditImage(contentId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(
        `/api/contents/${contentId}/upload-edit-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // ✅ Backend retorna: { success: true, data: { url, file_name, file_size, content_id } }
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro no upload de imagem:', error);
      throw error;
    }
  },

  /**
   * Salva as edições de um conteúdo Placid
   * - Regenera a imagem final via API Placid
   * - Atualiza o content no banco com histórico
   * 
   * @param {string} batchId - ID do batch
   * @param {string} contentId - ID do content
   * @param {Object} editData - Dados da edição { layers: Object, regenerate: boolean }
   * @returns {Promise<{success: boolean, data: Object}>}
   */
  async saveContentEdit(batchId, contentId, editData) {
    try {
      const response = await api.patch(
        `/api/contents/batches/${batchId}/contents/${contentId}/edit`,
        editData
      );

      // ✅ Backend retorna: { success: true, data: {...}, message: string }
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao salvar edição:', error);
      throw error;
    }
  },

  async saveCreatomateEdit(batchId, contentId, editData) {
    try {
      const response = await api.patch(
        `/api/contents/batches/${batchId}/contents/${contentId}/edit-creatomate`,
        editData
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao salvar edição Creatomate:', error);
      throw error;
    }
  },

  async saveTemplatedEdit(batchId, contentId, editData) {
    try {
      const response = await api.patch(
        `/api/contents/batches/${batchId}/contents/${contentId}/edit-templated`,
        editData
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao salvar edição Templated:', error);
      throw error;
    }
  },

  /**
   * Salva edicao de um content react_render.
   * - Re-substitui placeholders no jsx_template usando editable_fields
   * - Re-renderiza via Creative VM
   * - Atualiza content + edit_history
   *
   * @param {string} batchId
   * @param {string} contentId
   * @param {Object} editData - { editable_fields: {title,subtitle,cta,handle}, brand_image_url: string|null, regenerate: boolean }
   */
  async saveReactRenderEdit(batchId, contentId, editData) {
    try {
      const response = await api.patch(
        `/api/contents/batches/${batchId}/contents/${contentId}/edit-react-render`,
        editData
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao salvar edição React Render:', error);
      throw error;
    }
  },

  /**
   * Obtém o histórico de edições de um content
   * @param {string} batchId - ID do batch
   * @param {string} contentId - ID do content
   * @returns {Promise<{content_id: string, current_url: string, current_layers: Object, edit_history: Array}>}
   */
  async getContentEditHistory(batchId, contentId) {
    try {
      const response = await api.get(
        `/api/contents/batches/${batchId}/contents/${contentId}/history`
      );
      
      // ✅ Backend retorna: { data: { content_id, current_url, current_layers, edit_history } }
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      throw error;
    }
  },

  /**
   * Restaura uma versão anterior do content
   * @param {string} batchId - ID do batch
   * @param {string} contentId - ID do content
   * @param {number} historyIndex - Índice do histórico (0 = mais recente)
   * @returns {Promise<{success: boolean, data: Object}>}
   */
  async restoreContentVersion(batchId, contentId, historyIndex) {
    try {
      const response = await api.post(
        `/api/contents/batches/${batchId}/contents/${contentId}/revert/${historyIndex}`
      );
      
      // ✅ Backend retorna: { success: true, data: {...}, message: string }
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao restaurar versão:', error);
      throw error;
    }
  },

  /**
   * Helpers para formatação e cálculos
   */
  helpers: {
    /**
     * Calcula ETA para um modelo
     */
    calculateETA: (model, count) => {
      const avgTimes = {
        'dall-e-3': 8,
        'flux-1.1': 4,
        'nano-banana': 4,
        'sora': 45,
        'veo-3.1': 75,
        'hyperframes': 90,
        'placid': 3
      };

      const timePerItem = avgTimes[model] || 10;
      const totalSeconds = timePerItem * count;

      if (totalSeconds < 60) {
        return `~${totalSeconds}s`;
      }

      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      if (seconds === 0) {
        return `~${minutes}min`;
      }

      return `~${minutes}min ${seconds}s`;
    },

    /**
     * Calcula ETA total do batch
     */
    calculateBatchETA: (enabledModels, settings) => {
      let totalSeconds = 0;

      if (enabledModels.dall_e_3) {
        totalSeconds += 8 * (settings.dall_e_3_count || 0);
      }

      if (enabledModels.flux_11) {
        totalSeconds += 4 * (settings.flux_11_count || 0);
      }

      if (enabledModels.nano_banana) {
        totalSeconds += 4 * (settings.nano_banana_count || 0);
      }

      if (enabledModels.sora) {
        totalSeconds += 45 * (settings.sora_count || 0);
      }

      if (enabledModels.veo_31) {
        totalSeconds += 75 * (settings.veo_31_count || 0);
      }

      if (enabledModels.placid) {
        totalSeconds += 3 * (settings.placid_count || 0);
      }

      if (totalSeconds === 0) {
        return '0s';
      }

      if (totalSeconds < 60) {
        return `~${totalSeconds}s`;
      }

      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        
        if (remainingMinutes === 0) {
          return `~${hours}h`;
        }
        
        return `~${hours}h ${remainingMinutes}min`;
      }

      if (seconds === 0) {
        return `~${minutes}min`;
      }

      return `~${minutes}min ${seconds}s`;
    },

    /**
     * Formatar status do batch para UI
     */
    formatBatchStatus(batch) {
      if (!batch || !batch.progress) {
        return {
          text: 'Desconhecido',
          color: 'gray',
          icon: '❓',
          percentage: 0
        };
      }

      const { total, completed, failed, processing, pending } = batch.progress;

      if (batch.status === 'completed') {
        return {
          text: 'Completo',
          color: 'green',
          icon: '✅',
          percentage: 100
        };
      }

      if (batch.status === 'failed' || failed === total) {
        return {
          text: 'Falhou',
          color: 'red',
          icon: '❌',
          percentage: 0
        };
      }

      if (batch.status === 'pending') {
        return {
          text: 'Na fila',
          color: 'orange',
          icon: '⏳',
          percentage: 0
        };
      }

      if (processing > 0 || batch.status === 'processing') {
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
        return {
          text: `Processando (${completed}/${total})`,
          color: 'purple',
          icon: '🔄',
          percentage
        };
      }

      if (pending > 0) {
        return {
          text: 'Aguardando',
          color: 'orange',
          icon: '⏸️',
          percentage: Math.round((completed / total) * 100)
        };
      }

      return {
        text: 'Em andamento',
        color: 'blue',
        icon: '🔄',
        percentage: Math.round((completed / total) * 100)
      };
    },

    /**
     * Obter cor do modelo
     */
    getModelColor(model) {
      const colors = {
        'dall-e-3': 'from-green-400 to-emerald-500',
        'flux-1.1': 'from-blue-400 to-cyan-500',
        'nano-banana': 'from-yellow-400 to-orange-500',
        'sora': 'from-purple-400 to-pink-500',
        'veo-3.1': 'from-indigo-400 to-purple-500',
        'hyperframes': 'from-rose-400 to-pink-500',
        'placid': 'from-amber-400 to-orange-500'
      };
      return colors[model] || 'from-gray-500 to-gray-600';
    },

    /**
     * Obter badge do modelo
     */
    getModelBadge(model) {
      const badges = {
        'dall-e-3': {
          text: 'DALL-E 3',
          icon: '🎨',
          emoji: '🎨',
          color: 'bg-green-100 text-green-800'
        },
        'flux-1.1': {
          text: 'Flux-1.1',
          icon: '⚡',
          emoji: '⚡',
          color: 'bg-blue-100 text-blue-800'
        },
        'nano-banana': {
          text: 'Nano Banana',
          icon: '✨',
          emoji: '✨',
          color: 'bg-yellow-100 text-yellow-800'
        },
        'sora': {
          text: 'Sora',
          icon: '🎬',
          emoji: '🎬',
          color: 'bg-purple-100 text-purple-800'
        },
        'veo-3.1': {
          text: 'Veo 3.1',
          icon: '🎬',
          emoji: '🎬',
          color: 'bg-indigo-100 text-indigo-800'
        },
        'hyperframes': {
          text: 'Reels Animado',
          icon: '🎬',
          emoji: '🎬',
          color: 'bg-rose-100 text-rose-800'
        },
        'placid': {
          text: 'Placid',
          icon: '🎯',
          emoji: '🎯',
          color: 'bg-amber-100 text-amber-800'
        }
      };
      return badges[model] || { text: model, icon: '🤖', emoji: '🤖', color: 'bg-gray-100 text-gray-800' };
    },

    /**
     * Ícone por tipo de conteúdo
     */
    getContentTypeIcon(type) {
      const icons = {
        'image': '🖼️',
        'video': '🎬',
        'sale': '🏷️',
        'announcement': '📢',
        'marketing': '📊',
        'story': '📱',
        'banner': '🎯',
        'post': '📝'
      };
      return icons[type] || '📄';
    },

    /**
     * Validar configurações do batch
     */
    validateBatchConfig(config) {
      const errors = [];

      if (!config.user_prompt || config.user_prompt.trim().length < 10) {
        errors.push('O prompt deve ter pelo menos 10 caracteres');
      }

      if (!config.brand_config || !config.brand_config.name) {
        errors.push('Nome da marca é obrigatório');
      }

      const { enabled_models } = config;
      if (!enabled_models || !Object.values(enabled_models).some(v => v === true)) {
        errors.push('Selecione pelo menos um modelo de IA');
      }

      const { generation_settings } = config;
      if (enabled_models?.dall_e_3 && (!generation_settings?.dall_e_3_count || generation_settings.dall_e_3_count < 1)) {
        errors.push('DALL-E 3 requer pelo menos 1 conteúdo');
      }
      if (enabled_models?.flux_11 && (!generation_settings?.flux_11_count || generation_settings.flux_11_count < 1)) {
        errors.push('Flux-1.1 requer pelo menos 1 conteúdo');
      }
      if (enabled_models?.sora && (!generation_settings?.sora_count || generation_settings.sora_count < 1)) {
        errors.push('Sora requer pelo menos 1 conteúdo');
      }

      const total = 
        (enabled_models?.dall_e_3 ? generation_settings?.dall_e_3_count || 0 : 0) +
        (enabled_models?.flux_11 ? generation_settings?.flux_11_count || 0 : 0) +
        (enabled_models?.nano_banana ? generation_settings?.nano_banana_count || 0 : 0) +
        (enabled_models?.sora ? generation_settings?.sora_count || 0 : 0) +
        (enabled_models?.veo_31 ? generation_settings?.veo_31_count || 0 : 0) +
        (enabled_models?.placid ? generation_settings?.placid_count || 0 : 0);

      if (total === 0) {
        errors.push('Configure pelo menos 1 conteúdo para gerar');
      }

      if (total > 20) {
        errors.push('Máximo de 20 conteúdos por batch');
      }

      return {
        valid: errors.length === 0,
        errors
      };
    },

    /**
     * Gerar configuração padrão
     */
    getDefaultConfig() {
      return {
        user_prompt: '',
        brand_config: {
          name: '',
          description: '',
          style: 'moderno e profissional'
        },
        generation_settings: {
          dall_e_3_count: 4,
          flux_11_count: 4,
          nano_banana_count: 0,
          sora_count: 2,
          veo_31_count: 0,
          placid_count: 0,
          image_size: '1024x1024',
          image_style: 'vivid',
          image_quality: 'hd',
          video_width: 854,
          video_height: 480,
          video_duration: 5
        },
        enabled_models: {
          dall_e_3: true,
          flux_11: true,
          nano_banana: false,
          sora: true,
          veo_31: false,
          placid: false
        },
        content_types: ['marketing'],
        colors: ['moderno']
      };
    },

    /**
     * 🆕 Gerar configuração padrão para fluxo Instagram
     */
    getInstagramDefaultConfig() {
      return {
        generation_settings: {
          dall_e_3_count: 2,
          flux_11_count: 2,
          nano_banana_count: 0,
          sora_count: 0,
          veo_31_count: 0,
          placid_count: 0,
          image_size: '1024x1024',
          image_style: 'vivid',
          image_quality: 'hd'
        },
        enabled_models: {
          dall_e_3: true,
          flux_11: true,
          nano_banana: false,
          sora: false,
          veo_31: false,
          placid: false
        },
        content_types: ['marketing']
      };
    }
  }
};

// Export default mantendo compatibilidade
export default contentService;